import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, tap } from 'rxjs/operators';

import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { enumEntityType } from '@data-layer/enums/isf';
import { FormHelper } from '@helpers/form.helper';
import { identifierANIValidator, identifierEINValidator, identifierSSNValidator } from '@helpers/custom-validator';
import { IItemMask } from '@interfaces/item-mask';
import { CompaniesService } from '@services/companies.service';
import { EntitiesRepository } from '@data-layer/repositories';
import { CompanyModel } from '@data-layer/models/companies';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IIdentifierQualForAction } from '@shared/identifier-update/interfaces';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { isEqual, pick } from 'lodash-es';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { UserBetaFlagModel } from '@app/data-layer/models/user';

@Component({
  selector: 'identifier-entity-update',
  templateUrl: './identifier-entity-update.component.html',
  styleUrls: ['./identifier-entity-update.component.scss'],
})
export class IdentifierEntityUpdateComponent implements OnInit, OnDestroy {
  @Input() public formGroup: UntypedFormGroup = null;
  @Input() public code: enumEntityModelCode = null;
  @Input() public errors: IErrorForm = null;
  @Input() public type: enumEntityType;
  @Input() public identifierFieldName: 'identifier' | 'entityIdentifier' = 'entityIdentifier';
  @Input() public identifierQualifierFieldName: 'identifierQualifier' | 'entityIdentifierQualifier' = 'entityIdentifierQualifier';
  @Input() public needUpdateValidators: boolean;

  public identifierControl: AbstractControl;
  public dateOfBirthControl: AbstractControl;
  public identifierQualifierControl: AbstractControl;
  public enumIdentityQual: typeof enumIdentifierQualifier = enumIdentifierQualifier;

  public itemMask: IItemMask = {} as IItemMask;
  public helperIdentifier: string = null;
  public identitiesQual: IIdentifierQualForAction[] = [];
  public isShowDateOfBirth: boolean = false;
  public isfEntities: null | any[] = null;
  public identifierLoading: boolean = false;
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public get isIdentitiesQual() {
    return this.attachmentEntity?.[this.code]?.length;
  }

  private DEFAULT_MASK: string = '0{13}';
  private valueIdentifier: null | string = null;
  private attachmentEntity: any = {
    UP: ['EIN', 'SSN'],
    IM: ['EIN', 'SSN', 'ANI'],
    CN: ['EIN', 'SSN'],
    BY: ['EIN', 'SSN'],
    ST: [],
    SE: [],
    MF: [],
    CS: [],
    LG: [],
    ABI: [],
  };
  private maskArray: { [key: string]: IItemMask } = {
    ANI: {
      mask: '000000-00000',
      placeholder: 'YYDDPP-NNNNN',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.ANI',
      maskMaxLength: 11,
    },
    EIN: {
      mask: '00-0000000AA',
      placeholder: 'NN-NNNNNNNAA',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.EIN',
      maskMaxLength: 11,
    },
    SSN: {
      mask: '000-00-0000',
      placeholder: 'NNN-NN-NNNN',
      inputmode: 'numeric',
      maskErrorTooltip: 'MASK_ERROR.ONLY_DIGITS',
      maskMaxLength: 9,
    },
  };
  private company: CompanyModel;
  private companyId: CompanyModel['id'];

  private subscription: Subscription[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    private fh: FormHelper,
    private companiesService: CompaniesService,
    private entitiesRepository: EntitiesRepository,
  ) {
    this.itemMask = this.maskArray['EIN'] as IItemMask;
  }

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();
    this.setControls();
    const identitiesQual: IIdentifierQualForAction[] = Object.keys(this.enumIdentityQual).map((qual: any) => ({
      key: qual,
      value: this.enumIdentityQual[qual],
    }));

    this.identitiesQual = this.filterIdentitiesQualByIsfEntityCode(identitiesQual);
    this.subscription.push(
      this.companiesService.companyChange$.subscribe(() => {
        this.companyId = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;
      }),
      this.identifierQualifierControl.valueChanges.subscribe((val: enumIdentifierQualifier) => {
        if (!val) {
          return;
        }

        this.setIdentifierMaskAndPlaceHolder(val);
      }),
      this.identifierControl.valueChanges.subscribe((val: string) => {
        if (!this.needUpdateValidators) {
          return;
        }
        if (!!val) {
          if (!this.identifierQualifierControl.value) {
            this.identifierQualifierControl.setValidators(Validators.required);
            this.identifierQualifierControl.updateValueAndValidity();
          }
        } else {
          this.identifierQualifierControl.clearValidators();
          this.identifierQualifierControl.updateValueAndValidity();
        }
      }),
      this.identifierControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((value) => {
            if (!value && enumEntityModelCode.SHIP_TO === this.code) {
              this.identifierQualifierControl.reset();
            }
          }),
          pairwise(),
          filter(([pre, cur]) => {
            if (!pre || !cur) {
              return false;
            }
            return Math.abs(cur.length - (pre ? pre.length : 0)) === 1;
          }),
          debounceTime(600),
          distinctUntilChanged(),
          filter(([pre, cur]) => {
            return cur && cur.length >= 3;
          }),
          switchMap(([pre, cur]) => {
            if (this.valueIdentifier && isEqual(pre, cur) && isEqual(pre, this.valueIdentifier)) {
              return of(this.isfEntities);
            }
            this.identifierLoading = true;

            if (!this.companyId) {
              return of([]);
            }

            return this.entitiesRepository
              .searchByCompany(this.companyId, {
                type: this.code,
                identifier: cur,
              })
              .pipe(
                map((entityList) => {
                  return entityList.map((newEntityModel) => {
                    return new EntityModel({
                      ...(newEntityModel as any),
                      code: newEntityModel.entityCode as any,
                      entityId: newEntityModel.id,
                      entityIdentifier: newEntityModel.entityIdentifier,
                      entityIdentifierQualifier: newEntityModel.entityIdentifierQualifier,
                    });
                  });
                }),
              );
          }),
          map((value) => value),
          tap((values: any[]) => {
            if (values.length === 0) {
              this.valueIdentifier = this.identifierControl.value;
            } else {
              this.valueIdentifier = null;
            }
          }),
        )
        .subscribe((values: any[] | null) => {
          this.isfEntities = values;
          this.identifierLoading = false;
        }),
    );
    this.setIdentifierMaskAndPlaceHolder(this.identifierQualifierControl.value);
  }

  public setIdentitiesQual(qual: IIdentifierQualForAction) {
    this.identifierQualifierControl.patchValue(qual.value);
    this.cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((e) => e.unsubscribe());
  }

  public changeAddress(item: any) {
    const valueToPatch = pick(item, Object.keys(this.formGroup.value));

    if (valueToPatch.address) {
      this.fh.patchAddress(this.formGroup.get('address') as UntypedFormGroup, valueToPatch.address);
    } else {
      const countryForm = this.formGroup.get(['address', 'country']);
      const countryValue = countryForm.value;
      this.formGroup.get('address').reset();
      countryForm.patchValue(countryValue);
    }
    delete valueToPatch.address;

    this.formGroup.patchValue(valueToPatch);
    this.identifierControl.setValue(item.identifier);
    this.identifierQualifierControl.setValue(item.identifierQualifier);
  }

  public getIdentifierQualifierTranslationKey(qual: enumIdentifierQualifier): string {
    if (qual === enumIdentifierQualifier.ANI && this.isHasAssignedCbpRequest) {
      return 'OTHER';
    }

    return qual;
  }

  public get entityIdentifierLabel(): string {
    switch (this.formGroup.get('entityIdentifierQualifier').value) {
      case enumIdentifierQualifier.EIN:
        return 'LABELS.EIN_HINT';
      case enumIdentifierQualifier.SSN:
        return 'LABELS.SSN_HINT';
      case enumIdentifierQualifier.ANI:
        return 'LABELS.ANI_HINT';
      default:
        return 'LABELS.EIN_HINT';
    }
  }

  private setIdentifierMaskAndPlaceHolder(valueQual: enumIdentifierQualifier) {
    let validatorFn;

    switch (valueQual) {
      case this.enumIdentityQual.EIN:
        validatorFn = identifierEINValidator;
        break;
      case this.enumIdentityQual.SSN:
        validatorFn = identifierSSNValidator;
        break;
      case this.enumIdentityQual.ANI:
        validatorFn = identifierANIValidator;
        break;
      default:
        validatorFn = null;
        break;
    }

    this.setIdentitiesQualDependence(valueQual, validatorFn);

    if (valueQual === this.enumIdentityQual.SSN) {
      this.isShowDateOfBirth = true;
      this.dateOfBirthControl.updateValueAndValidity({ emitEvent: false });
    } else {
      this.isShowDateOfBirth = false;
      this.dateOfBirthControl.reset();
    }
  }

  private setIdentitiesQualDependence(valueQual: enumIdentifierQualifier, validatorFn?: () => ValidatorFn) {
    const identitiesQual = this.identitiesQual.find((e) => e.value === valueQual);
    const qual = (identitiesQual && identitiesQual.key) || null;
    this.itemMask = this.getItemMask(qual);

    this.helperIdentifier = !qual ? null : `FORM_HELPER.IDENTIFIER_${qual}`;
    if (this.needUpdateValidators) {
      if (!validatorFn) {
        this.identifierControl.clearValidators();
      } else {
        this.identifierControl.setValidators([Validators.required, validatorFn()]);
      }
    }
    this.identifierControl.updateValueAndValidity({ emitEvent: false });
  }

  private setControls(): void {
    this.identifierControl = this.formGroup.get(this.identifierFieldName);
    this.dateOfBirthControl = this.formGroup.get('dateOfBirth');
    this.identifierQualifierControl = this.formGroup.get(this.identifierQualifierFieldName);

    if (!this.identifierControl || !this.dateOfBirthControl || !this.identifierQualifierControl) {
      throw new Error(`You can't use this component!`);
    }
  }

  private getItemMask(key: string): IItemMask {
    const {
      mask = this.DEFAULT_MASK,
      placeholder = null,
      inputmode = 'text',
      maskMaxLength,
      maskErrorTooltip,
    } = this.maskArray[key] || this.maskArray['EIN'];

    return { mask, placeholder, inputmode, maskMaxLength, maskErrorTooltip };
  }

  private filterIdentitiesQualByIsfEntityCode(identitiesQual: IIdentifierQualForAction[]) {
    const attachmentEntity: string[] = this.attachmentEntity[this.code];
    const isAddANIQualifier =
      this.code === enumEntityModelCode.CONSIGNEE && this.company?.identifierQualifier === enumIdentifierQualifier.ANI;

    if (isAddANIQualifier) {
      attachmentEntity.push(enumIdentifierQualifier.ANI);
    }

    return identitiesQual.filter((qual) => attachmentEntity.indexOf(qual.key) !== -1);
  }

  private get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }
}
